import { useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { auth } from '../../lib/supabase'
import { createPublicSignup, type BillingInterval, type BillingTier } from '../../lib/services/billing'

const BILLING_TIERS: ReadonlyArray<{ tier: BillingTier; title: string; trialCta: string }> = [
  { tier: 'standalone', title: 'Standalone', trialCta: 'Start 14-day trial' },
  { tier: 'consultant', title: 'Consultant', trialCta: 'Start 14-day trial' },
  { tier: 'enterprise', title: 'Enterprise', trialCta: 'Create billing workspace' },
]

export function SignupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [form, setForm] = useState({
    organization_name: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    facility_name: '',
    facility_type: 'ASC',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    admin_email: '',
    password: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const tier = useMemo< BillingTier>(() => {
    const value = searchParams.get('checkout_tier')
    return value === 'consultant' || value === 'enterprise' ? value : 'standalone'
  }, [searchParams])
  const billingInterval = useMemo<BillingInterval>(() => (searchParams.get('billing_interval') === 'annual' ? 'annual' : 'monthly'), [searchParams])
  const tierCopy = BILLING_TIERS.find((entry) => entry.tier === tier) ?? BILLING_TIERS[0]

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    setIsSubmitting(true)
    setError(null)
    try {
      const signup = await createPublicSignup({
        ...form,
        tier,
        billing_interval: billingInterval,
      })
      const { error: signInError } = await auth.signInWithPassword({
        email: signup.admin_email,
        password: form.password,
      })
      if (signInError) {
        throw signInError
      }
      navigate(signup.next_path, { replace: true })
    } catch (signupError) {
      setError((signupError as Error).message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_#f6fbff,_#e9eff6_52%,_#e3ebf3)] px-4 py-8">
      <div className="mx-auto max-w-3xl">
        <div className="v11-section overflow-hidden">
          <div className="border-b border-slate-200 bg-white px-6 py-5">
            <div className="flex items-center gap-2">
              <div className="rounded-lg bg-[var(--brand-primary)] p-1.5 text-white">
                <Shield size={16} />
              </div>
              <span className="font-brand text-xl font-bold text-[var(--brand-deep)]">ASC Survey</span>
            </div>
            <h1 className="mt-4 font-brand text-3xl font-bold text-[var(--brand-deep)]">{tierCopy.trialCta}</h1>
            <p className="mt-2 text-sm text-slate-600">
              Create your {tierCopy.title.toLowerCase()} tenant, bootstrap the first facility, and land directly in onboarding.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5 px-6 py-6">
            <section className="space-y-3">
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Organization</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Organization name" required value={form.organization_name} onChange={(event) => setForm((current) => ({ ...current, organization_name: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Primary contact name" required value={form.contact_name} onChange={(event) => setForm((current) => ({ ...current, contact_name: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Contact email" required type="email" value={form.contact_email} onChange={(event) => setForm((current) => ({ ...current, contact_email: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Contact phone" value={form.contact_phone} onChange={(event) => setForm((current) => ({ ...current, contact_phone: event.target.value }))} />
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">First Facility</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Facility name" required value={form.facility_name} onChange={(event) => setForm((current) => ({ ...current, facility_name: event.target.value }))} />
                <select className="rounded-lg border border-slate-300 px-3 py-2 text-sm" value={form.facility_type} onChange={(event) => setForm((current) => ({ ...current, facility_type: event.target.value }))}>
                  <option value="ASC">ASC</option>
                  <option value="hospital_outpatient">Hospital Outpatient</option>
                  <option value="office_based">Office Based</option>
                </select>
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm md:col-span-2" placeholder="Street address" required value={form.address} onChange={(event) => setForm((current) => ({ ...current, address: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="City" required value={form.city} onChange={(event) => setForm((current) => ({ ...current, city: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="State (2-letter)" required value={form.state} onChange={(event) => setForm((current) => ({ ...current, state: event.target.value.toUpperCase() }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="ZIP code" required value={form.zip_code} onChange={(event) => setForm((current) => ({ ...current, zip_code: event.target.value }))} />
              </div>
            </section>

            <section className="space-y-3">
              <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Admin Login</h2>
              <div className="grid gap-3 md:grid-cols-2">
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Admin email" required type="email" value={form.admin_email} onChange={(event) => setForm((current) => ({ ...current, admin_email: event.target.value }))} />
                <input className="rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Password" required minLength={8} type="password" value={form.password} onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))} />
              </div>
            </section>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
              Plan: <span className="font-semibold text-slate-900 capitalize">{tier}</span> | Interval:{' '}
              <span className="font-semibold text-slate-900 capitalize">{billingInterval}</span>
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <button type="submit" disabled={isSubmitting} className="btn-primary px-5 py-2.5 text-sm disabled:opacity-60">
                {isSubmitting ? 'Creating account...' : tierCopy.trialCta}
              </button>
              <p className="text-sm text-slate-600">
                Already have an account?{' '}
                <Link to={`/login?checkout_tier=${encodeURIComponent(tier)}&billing_interval=${encodeURIComponent(billingInterval)}`} className="font-semibold text-[var(--brand-primary)] hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
